import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { Card, CardBody, Col, Row } from "reactstrap"

import ButtonTooltip from "components/Common/ButtonTooltip"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import ImageViewer from "components/Common/ImageViewer/ImageViewer"
import TableContainer from "components/Common/TableContainer"
import { getStockColumns } from "components/Common/stock-table/stockColumns"
import { downloadVehiclePdf } from "helpers/pdf_helper"
import { useDeleteVehicleStock, useVehicleStocks } from "hooks/stockHook"
import { useListParams } from "hooks/useListParams"
import { useResponsiveCollapse } from "hooks/useWindowSize"

export default function ShowroomStocksTab({ showroomId }) {
  const navigate = useNavigate()

  const [stock, setStock] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [selectedImageUrl, setSelectedImageUrl] = useState("")
  const [imageTitle, setImageTitle] = useState("Auction Sheet")

  const openImageViewer = url => {
    setViewerOpen(true)
    setSelectedImageUrl(url || "")
  }

  const { params, setPage, updateParams } = useListParams({
    page: 1,
    per_page: 10,
    search: "",
  })

  const queryParams = useMemo(
    () => ({
      showroom_id: showroomId,
      page: params.page,
      per_page: params.per_page,
      ...params,
    }),
    [params, showroomId]
  )

  const {
    data: stocksResponse,
    isLoading,
    refetch,
    isRefetching,
  } = useVehicleStocks(queryParams, Boolean(showroomId))

  const { mutateAsync: deleteStock, isPending: deleteLoading } =
    useDeleteVehicleStock()

  const stocksData = stocksResponse?.data?.vehicles?.data ?? []
  const paginationInfo = stocksResponse?.data?.vehicles ?? {}
  const totalStocks = paginationInfo?.total ?? 0

  const handleStockClick = useCallback(stockData => {
    if (stockData?.stock_id) {
      navigate(`/stock-add-edit/${stockData.stock_id}`)
    }
  }, [])

  const onClickDelete = useCallback(stockData => {
    setStock(stockData)
    setDeleteModal(true)
  }, [])

  const handleDeleteStock = useCallback(async () => {
    if (!stock?.id) return
    try {
      const res = await deleteStock(stock.id)
      toast.success(res?.message ?? "Stock deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (error) {
      console.error("Failed to delete stock:", error)
      toast.error("Failed to delete stock.")
    }
  }, [stock, deleteStock, refetch])

  const onClickDownload = async stockId => {
    try {
      await downloadVehiclePdf(stockId)
    } catch (error) {}
  }

  const { isMobile, toggleRow, isExpanded } = useResponsiveCollapse()

  const columns = useMemo(
    () =>
      getStockColumns({
        navigate,
        isMobile,
        toggleRow,
        isExpanded,
        handleStockClick,
        onClickDelete,
        onClickDownload,
        openImageViewer,
        setImageTitle,
      }),
    [navigate, isMobile, toggleRow, isExpanded]
  )

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteStock}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <ImageViewer
        images={[selectedImageUrl]}
        initialIndex={0}
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        title={imageTitle}
      />

      <Card className="border-0 shadow-sm">
        <CardBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0 fw-semibold">
              Vehicle Stocks{" "}
              <span className="badge badge-soft-info">{totalStocks}</span>
            </h5>
            <ButtonTooltip
              type="refresh"
              btnId="refreshShowroomStocks"
              onClick={refetch}
              loading={isRefetching}
              color="light"
              className="btn btn-light"
            />
          </div>

          <Row className="mb-3">
            <Col sm={6} lg={4}>
              <GlobalInputSearch
                value={params.search}
                onChange={val =>
                  updateParams(prev => ({
                    ...prev,
                    search: val,
                    page: 1,
                  }))
                }
              />
            </Col>
          </Row>

          <TableContainer
            columns={columns}
            data={stocksData}
            isPagination
            paginationInfo={paginationInfo}
            onPageChange={setPage}
            loading={isLoading || isRefetching}
            className="table-responsive"
          />
        </CardBody>
      </Card>
    </>
  )
}
